import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import {
  isTwoFactorChallenge,
  useAuth,
  useLogin,
  useVerifyTwoFactor,
  type TwoFactorChallenge,
} from '../features/auth/useAuth';
import { useForgotPassword, useResetPassword, useVerifyResetCode } from '../features/auth/passwordReset';
import { CosmicBackground } from '../features/auth/CosmicBackground';
import '../features/design-system/tokens.css';
import '../features/auth/cosmos.css';

const schema = z.object({
  // Одно поле на два способа: разбирать почту от телефона — работа бэкенда,
  // человек не должен выбирать «как я вхожу» до того, как его пустили.
  login: z.string().min(1, 'Введите почту или телефон'),
  password: z.string().min(1, 'Введите пароль'),
  remember: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

type Stage = 'login' | '2fa' | 'forgot' | 'code' | 'newpass';

const FALLBACK_START = '/';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, refetch } = useAuth();
  const login = useLogin();
  const verify = useVerifyTwoFactor();
  const forgot = useForgotPassword();
  const verifyCode = useVerifyResetCode();
  const resetPassword = useResetPassword();

  const [stage, setStage] = useState<Stage>('login');
  const [showPassword, setShowPassword] = useState(false);

  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);

  // Восстановление доступа
  const [resetPhone, setResetPhone] = useState('');
  const [phoneMask, setPhoneMask] = useState<string | null>(null);
  const [smsCode, setSmsCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { login: '', password: '', remember: true },
  });

  if (isLoading) return null;
  if (isAuthenticated && stage !== '2fa') {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    // ⛔ `prv3`: не прыгаем на назначенный экран мимо проверки — он может быть спрятан.
    // Решает корневой маршрут: назначенный, если открыт, иначе первый открытый.
    return <Navigate to={from ?? FALLBACK_START} replace />;
  }

  /**
   * Куда вести после успешного входа: откуда пришли, иначе корень.
   *
   * ⛔ `prv3` (владелец, 07.09.2026): назначенный роли стартовый экран больше не
   * подставляется здесь напрямую — он может быть СПРЯТАН, и человек приземлялся ровно
   * на закрытый экран. Решает корневой маршрут: назначенный, если открыт, иначе первый
   * открытый раздел, иначе честные слова.
   */
  const goInside = () => {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    navigate(from ?? FALLBACK_START, { replace: true });
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const result = await login.mutateAsync(values);
      if (isTwoFactorChallenge(result)) {
        setChallenge(result);
        setStage('2fa');
        return;
      }
      goInside();
    } catch (err: unknown) {
      const e = err as {
        response?: { status?: number; data?: { errors?: { login?: string[]; email?: string[] } } };
      };
      const apiErrors = e?.response?.data?.errors;
      // Бэкенд отдаёт ошибку на обоих ключах, пока идёт переезд: читаем оба,
      // чтобы сообщение не потерялось в окне между выкатами.
      const message = apiErrors?.login?.[0] ?? apiErrors?.email?.[0];
      if (message) {
        setError('login', { message });
      } else if (e?.response?.status === 419) {
        setError('login', { message: 'Сессия истекла, попробуйте ещё раз' });
      } else {
        setError('login', { message: 'Не удалось войти. Проверьте логин и пароль.' });
      }
    }
  };

  const onVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setTwoFactorError(null);
    try {
      await verify.mutateAsync({ challenge_id: challenge.challenge_id, code: code.trim() });
      await refetch();
      goInside();
    } catch (err: unknown) {
      const e2 = err as { response?: { status?: number; data?: { error?: string; message?: string } } };
      if (e2?.response?.status === 410) {
        setTwoFactorError('Сессия подтверждения истекла. Войдите заново.');
        setChallenge(null);
        setStage('login');
      } else if (e2?.response?.data?.error === 'invalid_code') {
        setTwoFactorError('Неверный код. Попробуйте код из следующего окна (до 30 секунд).');
      } else {
        setTwoFactorError(e2?.response?.data?.message ?? 'Не удалось войти.');
      }
    }
  };

  const onForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    try {
      const res = await forgot.mutateAsync(resetPhone);
      setPhoneMask(res.phone_mask);
      setSmsCode('');
      setStage('code');
    } catch {
      setResetError('Не получилось отправить код. Попробуйте ещё раз.');
    }
  };

  const onCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    try {
      const res = await verifyCode.mutateAsync({ phone: resetPhone, code: smsCode.trim() });
      setResetToken(res.reset_token);
      setStage('newpass');
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { errors?: { code?: string[] }; message?: string } } };
      setResetError(e2?.response?.data?.errors?.code?.[0] ?? 'Код неверный или устарел.');
    }
  };

  const onNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    try {
      await resetPassword.mutateAsync({
        reset_token: resetToken,
        password: newPassword,
        password_confirmation: newPassword2,
      });
      setResetDone(true);
      setStage('login');
    } catch (err: unknown) {
      const e2 = err as {
        response?: { data?: { errors?: { password?: string[]; reset_token?: string[] }; message?: string } };
      };
      const d = e2?.response?.data;
      setResetError(
        d?.errors?.password?.[0] ?? d?.errors?.reset_token?.[0] ?? d?.message ?? 'Не удалось сменить пароль.',
      );
    }
  };

  const backToLogin = () => {
    setStage('login');
    setResetError(null);
    setSmsCode('');
  };

  return (
    <div className="ops-skin relative min-h-screen overflow-hidden">
      <CosmicBackground />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="ops-authcard w-full max-w-[432px]">
          {stage === 'login' && (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Brand subtitle="Вход для сотрудников" />

              {resetDone && (
                <p className="mb-4 rounded-ops-md bg-ops-success-soft px-4 py-3 text-sm font-medium text-ops-success">
                  Пароль изменён. Войдите с новым паролем.
                </p>
              )}

              <Field label="Почта или телефон" error={errors.login?.message}>
                <input
                  type="text"
                  autoComplete="username"
                  autoFocus
                  placeholder="name@company.ru или +7 (999) 123-45-67"
                  className="ops-input"
                  disabled={isSubmitting}
                  {...register('login')}
                />
              </Field>

              <Field label="Пароль" error={errors.password?.message}>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="ops-input pr-11"
                    disabled={isSubmitting}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-ops-sm p-2 text-ops-text-tertiary transition-colors hover:bg-ops-primary-soft hover:text-ops-primary"
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </Field>

              <div className="mb-5 mt-4 flex items-center justify-between gap-3">
                <label className="flex cursor-pointer select-none items-center gap-2.5 text-sm text-ops-text-secondary">
                  <input type="checkbox" className="ops-check" disabled={isSubmitting} {...register('remember')} />
                  Запомнить меня
                </label>
                <button type="button" className="ops-link" onClick={() => setStage('forgot')}>
                  Забыли пароль?
                </button>
              </div>

              <button type="submit" className="ops-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Входим…' : 'Войти'}
              </button>

              <p className="mt-5 border-t border-ops-border pt-4 text-center text-[13px] text-ops-text-tertiary">
                Нет доступа? Попросите владельца прислать приглашение.
              </p>
            </form>
          )}

          {stage === '2fa' && (
            <form onSubmit={onVerifySubmit} noValidate>
              <Brand subtitle={useRecovery ? 'Резервный код' : 'Код из приложения'} />
              <p className="mb-5 text-sm text-ops-text-secondary">
                {useRecovery
                  ? 'Введите один из резервных кодов (формат XXXXX-XXXXX).'
                  : 'Введите шестизначный код из приложения-аутентификатора.'}
              </p>

              <Field label={useRecovery ? 'Резервный код' : 'Код подтверждения'} error={twoFactorError ?? undefined}>
                <input
                  type="text"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={useRecovery ? 'XXXXX-XXXXX' : '123 456'}
                  maxLength={useRecovery ? 16 : 6}
                  className="ops-input"
                  disabled={verify.isPending}
                />
              </Field>

              <button type="submit" className="ops-btn" disabled={verify.isPending || !code}>
                {verify.isPending ? 'Проверяю…' : 'Подтвердить'}
              </button>

              <button
                type="button"
                className="ops-link mx-auto mt-4 block"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setCode('');
                  setTwoFactorError(null);
                }}
              >
                {useRecovery ? 'Использовать код из приложения' : 'Использовать резервный код'}
              </button>
            </form>
          )}

          {stage === 'forgot' && (
            <form onSubmit={onForgotSubmit} noValidate>
              <BackRow onClick={backToLogin}>Назад ко входу</BackRow>
              <h1 className="mb-1.5 text-[22px] font-bold tracking-tight text-ops-text">Восстановление доступа</h1>
              <p className="mb-5 text-sm text-ops-text-secondary">
                Пришлём одноразовый код в СМС — по нему зададите новый пароль.
              </p>

              <Field label="Номер телефона" error={resetError ?? undefined}>
                <input
                  type="tel"
                  autoComplete="tel"
                  autoFocus
                  value={resetPhone}
                  onChange={(e) => setResetPhone(e.target.value)}
                  placeholder="+7 (___) ___-__-__"
                  className="ops-input"
                  disabled={forgot.isPending}
                />
              </Field>

              <button type="submit" className="ops-btn" disabled={forgot.isPending || !resetPhone.trim()}>
                {forgot.isPending ? 'Отправляю…' : 'Прислать код'}
              </button>

              {/* Про задержку сказано честно: оператор иногда копит сообщения
                  и выдаёт пачкой через несколько минут (наблюдение 13.08.2026).
                  Обещание «придёт за минуту» превращало это в «сломалось». */}
              <p className="mt-5 border-t border-ops-border pt-4 text-center text-[13px] text-ops-text-tertiary">
                Обычно код приходит за минуту, но оператор может задержать его на несколько минут.
                Код действует 15 минут. Если запросите новый, прежний перестанет работать.
              </p>
            </form>
          )}

          {stage === 'code' && (
            <form onSubmit={onCodeSubmit} noValidate>
              <BackRow onClick={() => setStage('forgot')}>Изменить номер</BackRow>
              <h1 className="mb-1.5 text-[22px] font-bold tracking-tight text-ops-text">Код из сообщения</h1>
              {/* Формулировка условная намеренно. Система не говорит, заведён
                  ли номер (иначе форма превращается в проверялку «кто есть в
                  системе»), поэтому обещать «отправили» нельзя: если номера
                  нет, человек будет ждать СМС, которой не будет, и решит, что
                  сломалось. Честный текст объясняет тишину заранее. */}
              <p className="mb-5 text-sm text-ops-text-secondary">
                Если номер {phoneMask ?? 'указан верно'} заведён в системе, код уже отправлен.
                <span className="mt-1 block text-ops-text-tertiary">
                  Не приходит? Проверьте, что этот номер вписан в вашем профиле — код уходит только
                  на сохранённый в системе номер.
                </span>
              </p>

              <Field label="Шесть цифр из СМС" error={resetError ?? undefined}>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength={6}
                  className="ops-input text-center text-xl font-bold tracking-[0.4em]"
                  disabled={verifyCode.isPending}
                />
              </Field>

              <button type="submit" className="ops-btn" disabled={verifyCode.isPending || smsCode.length < 6}>
                {verifyCode.isPending ? 'Проверяю…' : 'Продолжить'}
              </button>
            </form>
          )}

          {stage === 'newpass' && (
            <form onSubmit={onNewPasswordSubmit} noValidate>
              <BackRow onClick={backToLogin}>Отменить</BackRow>
              <h1 className="mb-1.5 text-[22px] font-bold tracking-tight text-ops-text">Новый пароль</h1>
              <p className="mb-5 text-sm text-ops-text-secondary">
                Минимум 8 знаков. Проверяем, что пароль не встречается в утечках.
              </p>

              <Field label="Новый пароль">
                <input
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="минимум 8 знаков"
                  className="ops-input"
                  disabled={resetPassword.isPending}
                />
              </Field>

              <Field label="Ещё раз новый" error={resetError ?? undefined}>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword2}
                  onChange={(e) => setNewPassword2(e.target.value)}
                  placeholder="повторите"
                  className="ops-input"
                  disabled={resetPassword.isPending}
                />
              </Field>

              <button
                type="submit"
                className="ops-btn"
                disabled={resetPassword.isPending || newPassword.length < 8 || newPassword !== newPassword2}
              >
                {resetPassword.isPending ? 'Сохраняю…' : 'Сохранить пароль'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Brand({ subtitle }: { subtitle: string }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-3.5">
        <img src="/logo-mark.png" alt="" aria-hidden="true" width={56} height={56} className="h-14 w-14 select-none" draggable={false} />
        <span className="text-[23px] font-bold tracking-tight text-ops-text">OpsMind</span>
      </div>
      <p className="mt-2 text-[15px] text-ops-text-secondary">{subtitle}</p>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="mb-4 block">
      <span className="mb-1.5 block text-[13px] font-semibold text-ops-text-secondary">{label}</span>
      {children}
      {error && <span className="mt-1.5 block text-[13px] text-ops-danger">{error}</span>}
    </label>
  );
}

function BackRow({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="ops-link mb-4 flex items-center gap-1.5">
      <ArrowLeft size={15} />
      {children}
    </button>
  );
}
